import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Subscription, filter, map, startWith } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { InstalarAppBotonComponent } from '../../../shared/components/instalar-app-boton/instalar-app-boton.component';
import { AuthService } from '../../services/auth.service';
import { IglesiasService } from '../../services/iglesias.service';
import { PushNotificacionesService } from '../../services/push-notificaciones.service';
import { ThemeService } from '../../services/theme.service';
import { UserEventsService } from '../../services/user-events.service';
import { UserStore } from '../../services/user-store.service';
import { routeFade } from '../../utils/route-fade.animation';

interface NavItem {
  label: string;
  icon: string;
  to: string;
}

const AGREGAR_PERSONA_ITEM: NavItem = {
  label: 'Agregar persona',
  icon: 'pi pi-user-plus',
  to: '/agregar-persona',
};

const EVENTOS_ITEM: NavItem = {
  label: 'Eventos',
  icon: 'pi pi-calendar',
  to: '/eventos',
};

/** Tag de la barra. Producción no muestra nada. */
export function etiquetaDeEntorno(entorno: 'local' | 'dev' | 'prod'): string {
  if (entorno === 'local') {
    return 'Local';
  }
  if (entorno === 'dev') {
    return 'DEV';
  }
  return '';
}

@Component({
  selector: 'app-shell-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, Button, Tag, InstalarAppBotonComponent],
  templateUrl: './shell-layout.component.html',
  styleUrl: './shell-layout.component.scss',
  animations: [routeFade],
})
export class ShellLayoutComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly userEvents = inject(UserEventsService);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly push = inject(PushNotificacionesService);
  readonly userStore = inject(UserStore);
  readonly theme = inject(ThemeService);
  readonly etiquetaEntorno = etiquetaDeEntorno(environment.entorno);
  readonly etiquetaEntornoStyle = {
    fontSize: '0.65rem',
    padding: '0.05rem 0.35rem',
    lineHeight: '1.1',
  };
  private iglesiaSub?: Subscription;

  readonly collapsed = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly graficosHabilitados = signal(false);
  readonly listaAdmin = signal(false);

  // En el drawer mobile el sidebar siempre se muestra expandido (con labels).
  readonly isRail = computed(() => this.collapsed() && !this.mobileMenuOpen());

  constructor() {
    effect(() => {
      this.currentPath();
      this.mobileMenuOpen.set(false);
    });
  }

  ngOnInit(): void {
    this.userEvents.connect();
    void this.push.refrescarEstado();
    const esLider = this.userStore.isLider();
    const esAdmin = this.userStore.isAdmin();
    if (esLider || esAdmin) {
      this.iglesiaSub = this.iglesiasService.getCurrentIglesia().subscribe({
        next: (iglesia) => {
          if (esLider && !esAdmin) {
            this.graficosHabilitados.set(iglesia.grafico);
          }
          if (esAdmin && !esLider) {
            this.listaAdmin.set(iglesia.listaAdmin === true);
          }
        },
        error: () => {
          this.graficosHabilitados.set(false);
          this.listaAdmin.set(false);
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.iglesiaSub?.unsubscribe();
    this.userEvents.disconnect();
  }

  readonly superItems: NavItem[] = [
    { label: 'Super panel', icon: 'pi pi-th-large', to: '/superpanel' },
  ];

  readonly adminItems: NavItem[] = [
    { label: 'Congregación', icon: 'pi pi-users', to: '/congregacion' },
    { label: 'Solicitudes', icon: 'pi pi-inbox', to: '/solicitudes' },
    { label: 'Gráficos', icon: 'pi pi-chart-bar', to: '/graficos' },
    { label: 'Papelera', icon: 'pi pi-trash', to: '/papelera' },
  ];

  readonly leaderItems: NavItem[] = [
    { label: 'Célula', icon: 'pi pi-sitemap', to: '/lider' },
    { label: 'Tomar lista', icon: 'pi pi-check-square', to: '/tomar-lista' },
    { label: 'Muelle', icon: 'pi pi-compass', to: '/muelle' },
    { label: 'Eventos', icon: 'pi pi-calendar', to: '/eventos' },
    AGREGAR_PERSONA_ITEM,
  ];

  /** Si también es líder, Eventos queda en el menú de líder, debajo de Muelle. */
  readonly visibleAdminItems = computed(() => {
    if (this.userStore.isLider()) {
      return this.adminItems;
    }
    const items = [...this.adminItems];
    if (!this.iglesiasService.eventosActivos()) {
      return items;
    }
    const papelera = items.findIndex((item) => item.to === '/papelera');
    items.splice(papelera + 1, 0, EVENTOS_ITEM);
    return items;
  });
  readonly visibleLeaderItems = computed(() => {
    const items = this.userStore.vistaIglesia()
      ? this.leaderItems.filter((item) => item.to !== '/lider' && item.to !== '/tomar-lista')
      : this.leaderItems;
    if (this.iglesiasService.eventosActivos()) {
      return items;
    }
    return items.filter((item) => item.to !== '/eventos');
  });

  /** Si también es líder, Agregar persona queda en el menú de líder. */
  readonly contadorItems = computed((): NavItem[] => {
    const items: NavItem[] = [{ label: 'Contador', icon: 'pi pi-calculator', to: '/contador' }];
    if (!this.userStore.isLider()) {
      items.push(AGREGAR_PERSONA_ITEM);
    }
    return items;
  });

  private readonly extraItems: NavItem[] = [
    { label: 'Perfil', icon: 'pi pi-user', to: '/perfil' },
    { label: 'Configuración', icon: 'pi pi-cog', to: '/configuracion' },
  ];

  /**
   * Dentro de configuración el fade lo hace la sección.
   * Si el navbar también cambia de clave, su animación se lleva el menú.
   */
  readonly pageFade = computed(() => {
    const path = this.currentPath();
    if (path === '/configuracion' || path.startsWith('/configuracion/')) {
      return '/configuracion';
    }
    return path;
  });

  readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  /** En mobile estas pantallas no llevan el padding del contenido. */
  readonly sinPaddingEnMobile = computed(() => {
    const path = this.currentPath();
    return path === '/solicitudes' || path === '/lider' || path === '/tomar-lista';
  });

  readonly pageTitle = computed(() => {
    const path = this.currentPath();
    if (path.startsWith('/editar-persona')) {
      return 'Editar persona';
    }
    const item = [
      ...this.superItems,
      ...this.visibleAdminItems(),
      ...this.leaderItems,
      ...this.contadorItems(),
      ...this.extraItems,
    ].find(
      (navItem) => path === navItem.to || path.startsWith(`${navItem.to}/`),
    );
    return item?.label ?? 'Panel';
  });

  readonly isPerfilActive = computed(() => {
    const path = this.currentPath();
    return path === '/perfil' || path.startsWith('/perfil/');
  });

  readonly isConfiguracionActive = computed(
    () =>
      this.currentPath() === '/configuracion' ||
      this.currentPath().startsWith('/configuracion/'),
  );

  toggleSidebar(): void {
    if (window.matchMedia('(max-width: 768px)').matches) {
      this.mobileMenuOpen.update((v) => !v);
    } else {
      this.collapsed.update((v) => !v);
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  logout(): void {
    this.push.desvincularAlSalir();
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}

